import { useState } from "react";
import {
    Box,
    Grid,
    Button,
    Typography,
    TextField,
    Paper,
    CircularProgress,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useNavigate } from "react-router";
import { AiActions } from "../../Services/_actions/index.js";
import { useSnackBar } from "../FlowBuilder/Common/SnackBar/index.tsx";



const TONE_OPTIONS = [
    {
        value: "professional",
        label: "Professional",
        emoji: "💼",
        desc: "Formal, authoritative",
    },
    {
        value: "friendly",
        label: "Friendly",
        emoji: "😊",
        desc: "Warm, approachable",
    },
    {
        value: "playful",
        label: "Playful",
        emoji: "🎉",
        desc: "Fun, energetic",
    },
    {
        value: "bold",
        label: "Bold",
        emoji: "⚡",
        desc: "Confident, direct",
    },
    {
        value: "empathetic",
        label: "Empathetic",
        emoji: "💬",
        desc: "Caring, supportive",
    },
    {
        value: "inspirational",
        label: "Inspirational",
        emoji: "🚀",
        desc: "Motivating, visionary",
    },
];

const FONT_OPTIONS = [
    "DM Sans",
    "Poppins",
    "Lato",
    "Montserrat",
    "Playfair Display",
    "Raleway",
    "Nunito",
    "Work Sans",
];

const PRESET_COLORS = [
    "#4962E5",
    "#9B59F5",
    "#E53E3E",
    "#DD6B20",
    "#D69E2E",
    "#38A169",
    "#3182CE",
    "#D53F8C",
];

export default function StepBrand({
    formData,
    updateData,
    onBack,
    onComplete,
    isFetching,
    recordExists,
    setRecordExists,
}) {
    const navigate = useNavigate();
    const action = AiActions();

    const { showSnackBar } = useSnackBar();

    const [errors, setErrors] = useState({});

    const [isSaving, setIsSaving] = useState(false);

    const userInfo = localStorage.getItem("userInfo");

    const parsedUser = userInfo
        ? JSON.parse(userInfo)
        : null;

    const organisationId =
        parsedUser?.profile?.organisation
            ?.organisation_id || "";

    const validate = () => {
        const e = {};

        if (!formData.brandTone) {
            e.brandTone =
                "Please choose a brand tone";
        }

        if (
            !formData.platformIntent?.trim()
        ) {
            e.platformIntent =
                "Please describe your intent";
        }

        return e;
    };

    const handleComplete = async () => {
        const validationErrors =
            validate();

        if (
            Object.keys(validationErrors)
                .length
        ) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});

        if (!organisationId) return;

        setIsSaving(true);

        try {
            const payload = {
                organization_id:
                    organisationId,

                // Company Details
                company_name:
                    formData?.companyName ||
                    "",

                industry:
                    formData?.industry || "",

                website:
                    formData?.website || "",

                description:
                    formData?.description ||
                    "",

                logo:
                    formData?.logoUrl || "",

                // Business Category
                sector:
                    formData?.sector || "",

                sub_sector:
                    formData?.subSector ||
                    "",

                category:
                    formData?.category ||
                    "",

                // Business Details
                business_type:
                    formData?.businessType ||
                    "",

                number_of_employees:
                    formData?.numEmployees ||
                    "",

                license_type:
                    formData?.licenseType ||
                    "",

                // Brand Preferences
                brand_preferences: {
                    brand_color:
                        formData?.brandColor ||
                        "",

                    brand_font:
                        formData?.brandFont ||
                        "",

                    brand_tone:
                        formData?.brandTone ||
                        "",

                    platform_intent:
                        formData?.platformIntent ||
                        "",
                },
            };

            let res;

            if (recordExists) {
                res =
                    await action.updateOrganizationInstruction(
                        payload
                    );
            } else {
                res =
                    await action.createOrganizationInstruction(
                        payload
                    );
            }

            if (
                res?.status === 200 ||
                res?.status === 201
            ) {
                if (!recordExists) {
                    setRecordExists?.(true);
                }
                showSnackBar(
                    "Workspace setup completed successfully",
                    "success"
                );

                onComplete?.(formData);

                navigate(
                    "/r/machadalostudio/ai",
                    {
                        state: {
                            companyName:
                                formData?.companyName,
                        },
                    }
                );
            }
        } catch (err) {
            console.error(
                "Save failed:",
                err
            );

            showSnackBar(
                "Failed to complete setup",
                "error"
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box overflow="auto" maxHeight="60vh" px={3} py={2}>
            {/* Title */}
            <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
                letterSpacing="0.1em"
            >
                Step 4 of 4
            </Typography>

            <Typography
                variant="h5"
                fontWeight={700}
                mt={0.5}
                mb={0.5}
            >
                Define your{" "}
                <Box
                    component="span"
                    color="primary.main"
                >
                    brand identity
                </Box>
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                mb={3}
            >
                Tell us how you look,
                feel and communicate —
                we'll carry it across
                every AI output.
            </Typography>

            {/* Brand Colour */}
            <Box mb={3}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                    letterSpacing="0.08em"
                    display="block"
                    mb={1}
                >
                    Brand Colour
                </Typography>

                <Box
                    sx={{
                        height: 44,
                        borderRadius: 2,
                        bgcolor:
                            formData.brandColor ||
                            "#4962E5",
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                            "flex-end",
                        px: 2,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "rgba(255,255,255,0.85)",
                            fontWeight: 700,
                            fontFamily:
                                "monospace",
                        }}
                    >
                        {(
                            formData.brandColor ||
                            "#4962E5"
                        ).toUpperCase()}
                    </Typography>
                </Box>

                <Grid
                    container
                    spacing={1}
                    alignItems="center"
                >
                    {PRESET_COLORS.map(
                        (c) => {
                            const selected =
                                formData.brandColor ===
                                c;

                            return (
                                <Grid
                                    item
                                    key={c}
                                >
                                    <Box
                                        onClick={() =>
                                            updateData(
                                                {
                                                    brandColor:
                                                        c,
                                                }
                                            )
                                        }
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 1.5,
                                            bgcolor:
                                                c,
                                            cursor:
                                                "pointer",
                                            border:
                                                "2px solid",
                                            borderColor:
                                                selected
                                                    ? "text.primary"
                                                    : "transparent",
                                        }}
                                    />
                                </Grid>
                            );
                        }
                    )}

                    <Grid item>
                        <Box
                            component="label"
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                border:
                                    "1.5px dashed",
                                borderColor:
                                    "grey.400",
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "center",
                                cursor:
                                    "pointer",
                                overflow:
                                    "hidden",
                                position:
                                    "relative",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                            >
                                +
                            </Typography>

                            <Box
                                component="input"
                                type="color"
                                value={
                                    formData.brandColor ||
                                    "#4962E5"
                                }
                                onChange={(
                                    e
                                ) =>
                                    updateData(
                                        {
                                            brandColor:
                                                e
                                                    .target
                                                    .value,
                                        }
                                    )
                                }
                                sx={{
                                    opacity: 0,
                                    position:
                                        "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    cursor:
                                        "pointer",
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Brand Font */}
            <Box mb={3}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                    letterSpacing="0.08em"
                    display="block"
                    mb={1}
                >
                    Brand Font
                </Typography>

                <Grid
                    container
                    spacing={1}
                >
                    {FONT_OPTIONS.map(
                        (f) => {
                            const selected =
                                formData.brandFont ===
                                f;

                            return (
                                <Grid
                                    item
                                    key={f}
                                >
                                    <Paper
                                        variant="outlined"
                                        onClick={() =>
                                            updateData(
                                                {
                                                    brandFont:
                                                        formData.brandFont ===
                                                            f
                                                            ? ""
                                                            : f,
                                                }
                                            )
                                        }
                                        sx={{
                                            px: 2,
                                            py: 0.75,
                                            cursor:
                                                "pointer",
                                            borderRadius: 2,
                                            borderColor:
                                                selected
                                                    ? "primary.main"
                                                    : "grey.200",
                                            bgcolor:
                                                selected
                                                    ? "primary.50"
                                                    : "background.paper",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={
                                                selected
                                                    ? 700
                                                    : 500
                                            }
                                            sx={{
                                                fontFamily: `${f}, sans-serif`,
                                            }}
                                        >
                                            {f}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        }
                    )}
                </Grid>
            </Box>

            {/* Brand Tone */}
            <Box mb={3}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                    letterSpacing="0.08em"
                    display="block"
                    mb={1}
                >
                    Brand Tone *
                </Typography>

                <Grid
                    container
                    spacing={1.5}
                >
                    {TONE_OPTIONS.map(
                        (t) => {
                            const selected =
                                formData.brandTone ===
                                t.value;

                            return (
                                <Grid
                                    item
                                    xs={6}
                                    sm={4}
                                    key={
                                        t.value
                                    }
                                >
                                    <Paper
                                        variant="outlined"
                                        onClick={() => {
                                            updateData(
                                                {
                                                    brandTone:
                                                        t.value,
                                                }
                                            );

                                            setErrors(
                                                (
                                                    e
                                                ) => ({
                                                    ...e,
                                                    brandTone:
                                                        "",
                                                })
                                            );
                                        }}
                                        sx={{
                                            p: 2,
                                            display:
                                                "flex",
                                            flexDirection:
                                                "column",
                                            alignItems:
                                                "center",
                                            gap: 0.5,
                                            cursor:
                                                "pointer",
                                            borderRadius: 2.5,
                                            textAlign:
                                                "center",
                                            borderColor:
                                                selected
                                                    ? "primary.main"
                                                    : "grey.200",
                                            bgcolor:
                                                selected
                                                    ? "primary.50"
                                                    : "background.paper",
                                        }}
                                    >
                                        <Typography
                                            fontSize={
                                                22
                                            }
                                        >
                                            {
                                                t.emoji
                                            }
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            fontWeight={
                                                selected
                                                    ? 700
                                                    : 600
                                            }
                                        >
                                            {
                                                t.label
                                            }
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {
                                                t.desc
                                            }
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        }
                    )}
                </Grid>

                {errors.brandTone && (
                    <Typography
                        variant="caption"
                        color="error"
                        mt={0.5}
                        display="block"
                    >
                        {
                            errors.brandTone
                        }
                    </Typography>
                )}
            </Box>

            {/* Platform Intent */}
            <Box mb={1}>
                <TextField
                    label="Why do you want to use this platform? *"
                    placeholder="Describe what you hope to achieve..."
                    value={
                        formData.platformIntent ||
                        ""
                    }
                    onChange={(e) => {
                        updateData({
                            platformIntent:
                                e.target
                                    .value,
                        });

                        setErrors(
                            (err) => ({
                                ...err,
                                platformIntent:
                                    "",
                            })
                        );
                    }}
                    error={
                        !!errors.platformIntent
                    }
                    helperText={
                        errors.platformIntent
                    }
                    fullWidth
                    multiline
                    rows={3}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Box>

            {/* Footer */}
            <Box
                display="flex"
                justifyContent="space-between"
                mt={3}
                pt={2.5}
                borderTop="1px solid"
                borderColor="grey.100"
            >
                <Button
                    variant="outlined"
                    startIcon={
                        <ArrowBackIcon />
                    }
                    onClick={onBack}
                    disabled={
                        isSaving ||
                        isFetching
                    }
                    sx={{
                        borderRadius: 2,
                        textTransform:
                            "none",
                        fontWeight: 600,
                    }}
                >
                    Back
                </Button>

                <Button
                    variant="contained"
                    startIcon={
                        isSaving ? (
                            <CircularProgress
                                size={18}
                                sx={{
                                    color: "#fff",
                                }}
                            />
                        ) : (
                            <CheckCircleIcon />
                        )
                    }
                    onClick={
                        handleComplete
                    }
                    disableElevation
                    color="success"
                    disabled={
                        isSaving ||
                        isFetching
                    }
                    sx={{
                        borderRadius: 2,
                        textTransform:
                            "none",
                        fontWeight: 600,
                        px: 3,
                    }}
                >
                    {isSaving
                        ? "Saving..."
                        : "Complete Setup"}
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => {
                        navigate("/r/machadalostudio/ai");
                    }}
                    sx={{
                        borderRadius: 2,
                        textTransform:
                            "none",
                        fontWeight: 600,
                    }}
                >
                    Enter Workspace
                </Button>
            </Box>
        </Box>
    );
}
